import * as React from 'react'
import * as Yup from 'yup'
import {
  Accordion,
  Autocomplete,
  Button,
  createStyles,
  Form,
  Grid,
  KeyboardDatePicker,
  Loader,
  makeStyles,
  MuiLocalizationProvider,
  Theme,
  useTheme,
  MuiAdapterDateFns,
  TextField,
  TextFieldProps
} from 'cs-kevlar-react'
import { Formik } from 'formik'
import { FormikTextField } from 'components/FormikTextField'
import compareFunc from 'compare-func'
import { useAuditApi, useAuth } from '../../hooks'
import { AuditSearchOption, WebAdminUser } from '../../api'
import { ArrowDropDown } from 'cs-kevlar-react/lib/es/icons'

const useStyles = makeStyles((_: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    input: {
      minWidth: '0',
      marginTop: '0',
    },
  }),
)

interface Option {
  key: string
  value: any
  label: string
}

export const AllOption: Option = {
  key: '-1',
  label: 'All',
  value: '-1',
}

const mapSearchOptions = (options: AuditSearchOption[]): Option[] => {
  const mappedOptions = options.map((option) => ({
    key: option.id,
    label: option.name,
    value: option.id,
  }))

  return mappedOptions.sort(compareFunc((x) => x.label))
}

export interface SearchOptions {
  startDate: Date
  endDate: Date
  customerAgencyId: string
  licensedAgencyId: string
  userId: string
  eventType: string
}

export const ValidationSchema = Yup.object().shape({
  startDate: Yup.date().required('Required'),
  endDate: Yup.date().required('Required'),
  customerAgencyId: Yup.string().required('Required').nullable(),
  licensedAgencyId: Yup.string().required('Required').nullable(),
  userId: Yup.string().required('Required').nullable(),
  eventType: Yup.string().required('Required').nullable(),
})

const evalAllowAgencyOptions = (user: WebAdminUser | null): boolean =>
  user?.isSupport == true && !user.isAssisting
export interface SearchFormProps {
  initialSearchOptions: SearchOptions
  onSearchRequested: (options: SearchOptions) => void
}

export const SearchForm: React.FunctionComponent<SearchFormProps> = ({
  initialSearchOptions,
  onSearchRequested,
}) => {
  const theme = useTheme<Theme>()
  const classes = useStyles({})

  const { user } = useAuth()
  const auditApi = useAuditApi()

  const allowAgencyOptions = evalAllowAgencyOptions(user)

  const [customerAgencyOptions, setCustomerAgencyOptions] = React.useState<
    Option[]
  >([])
  const [licensedAgencyOptions, setLicensedAgencyOptions] = React.useState<
    Option[]
  >([])
  const [licensedUserOptions, setLicensedUserOptions] = React.useState<
    Option[]
  >([])
  const [supportUserOptions, setSupportUserOptions] = React.useState<Option[]>(
    [],
  )
  const [eventTypeOptions, setEvenTypeOptions] = React.useState<Option[]>([])

  const [
    isLoadingCustomerAgencies,
    setIsLoadingCustomerAgencies,
  ] = React.useState(false)
  const [
    isLoadingLicensedAgencies,
    setIsLoadingLicensedAgencies,
  ] = React.useState(false)
  const [isLoadingLicensedUsers, setIsLoadingLicensedUsers] = React.useState(
    false,
  )
  const [isLoadingSupportUsers, setIsLoadingSupportUsers] = React.useState(
    false,
  )

  const [isLoadingEventTypes, setIsLoadingEventTypes] = React.useState(false)

  const loadCustomerAgencies = () => {
    setIsLoadingCustomerAgencies(true)

    auditApi.getCustomerAgencySearchOptions().then((response) => {
      const results = mapSearchOptions(response.data)
      setCustomerAgencyOptions(results)
      setIsLoadingCustomerAgencies(false)

      // Options will be disabled for user so load their options
      if (!allowAgencyOptions && !!results[0])
        loadLicensedAgencies(results[0].key)
    })
  }

  const loadLicensedAgencies = (customerAgencyId: string) => {
    setIsLoadingLicensedAgencies(true)

    if (!customerAgencyId || customerAgencyId === AllOption.value) {
      setLicensedAgencyOptions([])
      setIsLoadingLicensedAgencies(false)
      return
    }

    auditApi
      .getLicensedAgencySearchOptions(customerAgencyId)
      .then((response) => {
        const results = mapSearchOptions(response.data)
        setLicensedAgencyOptions(results)
        setIsLoadingLicensedAgencies(false)

        // Options will be disabled for user so load their options
        if (!allowAgencyOptions && !!results[0])
          loadLicensedUsers(customerAgencyId, results[0].key)
      })
  }

  const loadLicensedUsers = (
    customerAgencyId: string,
    licensedAgencyId: string,
  ) => {
    setIsLoadingLicensedUsers(true)

    if (
      !customerAgencyId ||
      customerAgencyId == AllOption.value ||
      !licensedAgencyId ||
      licensedAgencyId === AllOption.value
    ) {
      setLicensedUserOptions([])
      setIsLoadingLicensedUsers(false)
      return
    }

    auditApi
      .getLicensedUserSearchOptions(customerAgencyId, licensedAgencyId)
      .then((response) => {
        const results = mapSearchOptions(response.data)
        setLicensedUserOptions(results)
        setIsLoadingLicensedUsers(false)
      })
  }

  const loadSupportUsers = () => {
    setIsLoadingSupportUsers(true)

    auditApi.getSupportUserSearchOptions().then((response) => {
      const results = mapSearchOptions(response.data)
      setSupportUserOptions(results)
      setIsLoadingSupportUsers(false)
    })
  }

  const loadEventTypes = () => {
    setIsLoadingEventTypes(true)

    auditApi.getEventTypeSearchOptions().then((response) => {
      const results = mapSearchOptions(response.data)
      setEvenTypeOptions(results)
      setIsLoadingEventTypes(false)
    })
  }

  const allCustomerAgencyOptions = React.useMemo(
    () =>
      allowAgencyOptions
        ? [AllOption, ...customerAgencyOptions]
        : customerAgencyOptions,
    [allowAgencyOptions, customerAgencyOptions],
  )

  const allLicensedAgencyOptions = React.useMemo(
    () =>
      allowAgencyOptions
        ? [AllOption, ...licensedAgencyOptions]
        : licensedAgencyOptions,
    [allowAgencyOptions, licensedAgencyOptions],
  )

  const allUserOptions = React.useMemo(
    () => [AllOption, ...supportUserOptions, ...licensedUserOptions],
    [licensedUserOptions, supportUserOptions],
  )

  const allEventTypeOptions = React.useMemo(
    () => [AllOption, ...eventTypeOptions],
    [eventTypeOptions],
  )

  const isLoadingUsers = isLoadingLicensedUsers || isLoadingSupportUsers

  const isLoadingOptions =
    isLoadingCustomerAgencies || isLoadingLicensedAgencies || isLoadingUsers

  const getPopupIcon = (isLoading: boolean) => {
    return isLoading ? (
      <Loader size="sm" disableVerticalGutters />
    ) : (
      <ArrowDropDown />
    )
  }

  React.useEffect(() => {
    loadCustomerAgencies()
    loadSupportUsers()
    loadEventTypes()
  }, [])

  return (
    <React.Fragment>
      <Accordion id="Audit::SearchForm" title="Search" initiallyExpanded>
        <Formik
          initialValues={initialSearchOptions}
          validationSchema={ValidationSchema}
          onSubmit={(values, _) => onSearchRequested(values)}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit}>
              <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
                <Grid container spacing={8}>
                  <Grid item xs={6} sm={3} md={2}>
                    <KeyboardDatePicker 
                      className={classes.input}
                      label="Start Date"
                      value={formikProps.values.startDate}
                      inputFormat="MM/dd/yyyy"
                      onChange={(value: any) =>
                        formikProps.setFieldValue('startDate', value)
                      }
                      renderInput={(params: TextFieldProps) => <TextField className={classes.input} {...params} />}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <KeyboardDatePicker
                      className={classes.input}
                      label="End Date"
                      value={formikProps.values.endDate}
                      inputFormat="MM/dd/yyyy"
                      onChange={(value: any) =>
                        formikProps.setFieldValue('endDate', value)
                      }
                      renderInput={(params: TextFieldProps) => <TextField className={classes.input} {...params} />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      id="SearchForm::CustomerAgencyId"
                      className={classes.input}
                      freeSolo={false}
                      disabled={
                        isLoadingCustomerAgencies || !allowAgencyOptions
                      }
                      disableClearable
                      options={allCustomerAgencyOptions}
                      getOptionLabel={(option) => option.label}
                      value={
                        allCustomerAgencyOptions.find(
                          (p) => p.key == formikProps.values.customerAgencyId,
                        ) ??
                        allCustomerAgencyOptions[0] ??
                        AllOption
                      }
                      onChange={(_, newValue) => {
                        formikProps.setFieldValue(
                          'customerAgencyId',
                          newValue?.key,
                        )
                        formikProps.setFieldValue(
                          'licensedAgencyId',
                          AllOption.value,
                        )
                        formikProps.setFieldValue('userId', AllOption.value)

                        loadLicensedAgencies(newValue.key)
                        loadLicensedUsers(
                          formikProps.values.customerAgencyId,
                          newValue.key,
                        )
                      }}
                      onBlur={formikProps.handleBlur}
                      popupIcon={getPopupIcon(isLoadingCustomerAgencies)}
                      renderInput={(params) => (
                        <FormikTextField
                          {...params}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
                          }}
                          label="Customer"
                          formikProps={formikProps}
                          fieldName="customerAgencyId"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      id="SearchForm::LicensedAgencyId"
                      className={classes.input}
                      freeSolo={false}
                      disabled={
                        isLoadingLicensedAgencies || !allowAgencyOptions
                      }
                      disableClearable
                      options={allLicensedAgencyOptions}
                      getOptionLabel={(option) => option.label}
                      value={
                        allLicensedAgencyOptions.find(
                          (p) => p.key == formikProps.values.licensedAgencyId,
                        ) ??
                        allLicensedAgencyOptions[0] ??
                        AllOption
                      }
                      onChange={(_, newValue) => {
                        formikProps.setFieldValue(
                          'licensedAgencyId',
                          newValue.key,
                        )
                        formikProps.setFieldValue('userId', AllOption.value)
                        loadLicensedUsers(
                          formikProps.values.customerAgencyId,
                          newValue.key,
                        )
                      }}
                      onBlur={formikProps.handleBlur}
                      popupIcon={getPopupIcon(isLoadingLicensedAgencies)}
                      renderInput={(params) => (
                        <FormikTextField
                          {...params}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
                          }}
                          label="Agency"
                          formikProps={formikProps}
                          fieldName="licensedAgencyId"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      id="SearchForm::UserId"
                      className={classes.input}
                      freeSolo={false}
                      disabled={isLoadingUsers}
                      disableClearable
                      options={allUserOptions}
                      getOptionLabel={(option) => option.label}
                      value={
                        allUserOptions.find(
                          (p) => p.key == formikProps.values.userId,
                        ) ?? AllOption
                      }
                      onChange={(_, newValue) => {
                        formikProps.setFieldValue('userId', newValue.key)
                      }}
                      onBlur={formikProps.handleBlur}
                      popupIcon={getPopupIcon(isLoadingUsers)}
                      renderInput={(params) => (
                        <FormikTextField
                          {...params}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
                          }}
                          label="User"
                          formikProps={formikProps}
                          fieldName="userId"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={8}>
                    <Autocomplete
                      id="SearchForm::EventType"
                      className={classes.input}
                      freeSolo={false}
                      disabled={isLoadingEventTypes}
                      disableClearable
                      options={allEventTypeOptions}
                      getOptionLabel={(option) => option.label}
                      value={
                        allEventTypeOptions.find(
                          (p) => p.label == formikProps.values.eventType,
                        ) ?? AllOption
                      }
                      onChange={(_, newValue) => {
                        formikProps.setFieldValue('eventType', newValue.label != 'All' ? newValue.label : '-1')
                      }}
                      onBlur={formikProps.handleBlur}
                      popupIcon={getPopupIcon(isLoadingEventTypes)}
                      renderInput={(params) => (
                        <FormikTextField
                          {...params}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
                          }}
                          label="Event"
                          formikProps={formikProps}
                          fieldName="eventType"
                          fullWidth
                        />
                      )}
                    />

                    <Grid container spacing={8} justifyContent="flex-end">
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => formikProps.resetForm()}
                          disabled={isLoadingOptions}>
                          Reset
                        </Button>
                      </Grid>

                      <Grid item>
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={() => formikProps.submitForm()}
                          disabled={isLoadingOptions}
                        >
                          Search
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </MuiLocalizationProvider>
            </Form>
          )}
        </Formik>
      </Accordion>
    </React.Fragment>
  );
}
